import * as THREE from 'three';
import { CLOTH_BIAS, CLOTH_GAP } from './cloth-collision.js';
import { SKIN_VOLUME } from './volume-skin.js';
import { figureTexels, reserveTexels, touchTexels, withFigureTexels } from './figure-texels.js';

const INDICES = 'clothCageIndex', WEIGHTS = 'clothCageWeight';
/**
 * Largest drift of a leg capsule or bone matrix value from the pose last solved that leaves the cloth as it is: a millimetre.
 * Idle breathing crosses it a few times a second instead of every frame; the cloth's offset from the skinned pose is off by no more.
 */
const STILL = 1e-3;
/**
 * Pieces sharing a key share one cage. `columns` picks the grid columns the cage is built on (a flat strip's front edges) and `rows`
 * caps its rows; by default it spans up to 13 columns and 8 rows.
 */
export type CageSpec = string | { key: string; columns: number[]; rows: number };
const faceSamples = [[1 / 3, 1 / 3, 1 / 3], [.5, .5, 0], [0, .5, .5], [.5, 0, .5]];
interface Piece { geometry: THREE.BufferGeometry; rows: number; columns: number; key: string; stiff: boolean; pick?: number[]; most: number }
interface Node { geometry?: THREE.BufferGeometry; rest: THREE.Vector3; target: THREE.Vector3; point: THREE.Vector3; normal: THREE.Vector3; targetNormal: THREE.Vector3; skinNormal: THREE.Vector3; vertex: number; gap: number; pinned: boolean; mesh?: THREE.SkinnedMesh }
interface Cage { nodes: Node[]; triangles: number[]; edges: [number, number, number, number][]; offset: number; seam: [number, number][]; faceGaps: number[]; drops: [number, number][]; iterations: number }

/**
 * Small, pose-driven cloth panels. Only cage points solve contact and length constraints. Each point's offset and normal change ride in
 * the figure's bone texture (figure-texels.ts) when a skeleton is given, two texels a point, so materials need no uniforms of their own.
 */
export function createClothCages(chain: THREE.Vector4[], skeleton?: THREE.Skeleton) {
  const pieces: Piece[] = [], cages: Cage[] = [];
  /** Eight floats a point: its offset, then its normal change (each xyz and a spare). */
  let data = new Float32Array(), base = -1, ready = false;
  const p = new THREE.Vector3(), direction = new THREE.Vector4();
  const contactNormal = new THREE.Vector3(), contactBefore = new THREE.Vector3();
  const skinBones = new Set<THREE.Bone>();
  let snapshot = new Float64Array();
  function register(geometry: THREE.BufferGeometry, rows: number, columns: number, cage: CageSpec, stiff = false) {
    pieces.push({ geometry, rows, columns, stiff, ...typeof cage === 'string' ? { key: cage, most: 8 } : { key: cage.key, pick: cage.columns, most: cage.rows } });
  }
  function prepare() {
    if (!pieces.length) return;
    const groups = new Map<string, Piece[]>();
    for (const piece of pieces) { const group = groups.get(piece.key) ?? []; group.push(piece); groups.set(piece.key, group); }
    let total = 0;
    for (const group of groups.values()) {
      // The largest piece is the panel; a tie goes to the longer drop, so a strip and not its shorter tip carries the cage.
      const span = (piece: Piece) => { const y = piece.geometry.getAttribute('position'); let low = Infinity, high = -Infinity; for (let i = 0; i < y.count; i++) { low = Math.min(low, y.getY(i)); high = Math.max(high, y.getY(i)); } return [low, high]; };
      const height = (piece: Piece) => { const [low, high] = span(piece); return high - low; };
      const source = group.reduce((a, b) => a.rows * a.columns !== b.rows * b.columns ? (a.rows * a.columns > b.rows * b.columns ? a : b) : height(a) >= height(b) ? a : b);
      // Bands stacked down a garment (mail or hide rows, each a short loft) join into one panel; trim lying over a band does not.
      const frame = [source], covered = [span(source)];
      for (const piece of [...group].sort((a, b) => b.rows * b.columns - a.rows * a.columns)) {
        if (frame.includes(piece) || piece.columns !== source.columns) continue;
        const [low, high] = span(piece), overlap = covered.reduce((sum, [a, b]) => sum + Math.max(0, Math.min(high, b) - Math.max(low, a)), 0);
        if (overlap < (high - low) * .25) { frame.push(piece); covered.push([low, high]); }
      }
      const rowAt = (piece: Piece, row: number) => { const position = piece.geometry.getAttribute('position'); let y = 0; for (let col = 0; col < piece.columns; col++) y += position.getY(row * piece.columns + col); return y / piece.columns; };
      // Rows keep the source's own order, so the cage's winding (and its normal change) matches the cloth.
      const rising = rowAt(source, source.rows - 1) > rowAt(source, 0);
      const allRows = frame.flatMap(piece => Array.from({ length: piece.rows }, (_, row) => ({ piece, row, y: rowAt(piece, row) }))).sort((a, b) => rising ? a.y - b.y : b.y - a.y);
      const rows = Math.min(source.most, allRows.length), columns = source.pick?.length ?? Math.min(13, source.columns), nodes: Node[] = [], triangles: number[] = [], edges: Cage['edges'] = [], seam: Cage['seam'] = [];
      const rowIndices = Array.from({ length: rows }, (_, i) => allRows[Math.round(i * (allRows.length - 1) / (rows - 1))]);
      const colIndices = source.pick ?? Array.from({ length: columns }, (_, i) => Math.round(i * (source.columns - 1) / (columns - 1)));
      const top = rising ? rows - 1 : 0;
      for (let r = 0; r < rows; r++) for (let c = 0; c < columns; c++) {
        const { piece, row } = rowIndices[r], vertex = row * piece.columns + colIndices[c], rest = new THREE.Vector3().fromBufferAttribute(piece.geometry.getAttribute('position'), vertex);
        const gap = piece.geometry.getAttribute(CLOTH_GAP).getX(vertex) - CLOTH_BIAS;
        nodes.push({ geometry: piece.geometry, rest, target: rest.clone(), point: rest.clone(), normal: new THREE.Vector3(), targetNormal: new THREE.Vector3(), skinNormal: new THREE.Vector3(), vertex, gap, pinned: r === top });
      }
      // Armor cloth (tabards, flaps, tassets) is heavier than a gown: its two-step links barely shorten, so a knee lifting
      // the hem turns the whole panel up instead of folding the hem back over the row above it.
      const edge = (a: number, b: number, shortest = .85) => edges.push([a, b, nodes[a].rest.distanceTo(nodes[b].rest), shortest]), bend = source.stiff ? .97 : .85;
      for (let r = 0; r < rows; r++) for (let c = 0; c < columns; c++) {
        const a = r * columns + c;
        if (c + 1 < columns) edge(a, a + 1);
        if (r + 1 < rows) edge(a, a + columns);
        // Two-step links resist narrow bulges while still allowing broad folds.
        if (c + 2 < columns) edge(a, a + 2, bend);
        if (r + 2 < rows) edge(a, a + columns * 2, bend);
        if (r + 1 < rows && c + 1 < columns) { edge(a, a + columns + 1); edge(a + 1, a + columns); triangles.push(a, a + columns, a + 1, a + 1, a + columns, a + columns + 1); }
      }
      for (let r = 0; r < rows; r++) if (nodes[r * columns].rest.distanceToSquared(nodes[(r + 1) * columns - 1].rest) < 1e-8) seam.push([r * columns, (r + 1) * columns - 1]);
      const faceGaps: number[] = [], testPoint = new THREE.Vector3();
      // A point can start within its gap of these four coarse capsules though the finer leg chain leaves it clear (a thin goblin's rags).
      // Preserve that resting allowance rather than changing the standing garment.
      const allowance = (at: THREE.Vector3, normal: THREE.Vector3, gap: number) => {
        const moves = (g: number) => { testPoint.copy(at); contact(testPoint, normal, g); return testPoint.distanceToSquared(at) > 1e-10; };
        if (!moves(gap)) return gap;
        let low = -Math.max(...chain.map(v => v.w)), high = gap;
        for (let step = 0; step < 14; step++) { const mid = (low + high) / 2; if (moves(mid)) high = mid; else low = mid; }
        return low - 1e-5;
      };
      for (const node of nodes) node.gap = allowance(node.rest, contactNormal.fromBufferAttribute(node.geometry!.getAttribute('normal'), node.vertex), node.gap);
      // Coarse chords can start inside a capsule even when the authored curved surface is clear.
      for (let t = 0; t < triangles.length; t += 3) for (const weights of faceSamples) {
        p.set(0, 0, 0); contactNormal.set(0, 0, 0); let gap = 0;
        for (let c = 0; c < 3; c++) {
          const node = nodes[triangles[t + c]]; p.addScaledVector(node.rest, weights[c]); gap += node.gap * weights[c];
          contactBefore.fromBufferAttribute(node.geometry!.getAttribute('normal'), node.vertex); contactNormal.addScaledVector(contactBefore, weights[c]);
        }
        faceGaps.push(allowance(p, contactNormal.normalize(), gap));
      }
      // Armor cloth hangs: each point stays below the one above it along the cloth, so a knee lifting a row turns the panel
      // up rather than folding that row back over the one it hangs from.
      const drops: Cage['drops'] = [];
      // Listed from the pinned row down, so one pass settles each row under its final upper row.
      if (source.stiff) for (let step = 0; step < rows - 1; step++) for (let c = 0; c < columns; c++) {
        const r = rising ? rows - 1 - step : step, below = rising ? r - 1 : r + 1; drops.push([r * columns + c, below * columns + c]);
      }
      // Stiff armor cloth settles in half the solve; a little leg showing through it in a deep stride is accepted.
      const cage = { nodes, triangles, edges, offset: total, seam, faceGaps, drops, iterations: source.stiff ? 4 : 8 }; cages.push(cage); total += nodes.length;
      // All decorative bands and piping in the panel use the same deformation field.
      const triangle = new THREE.Triangle(), closest = new THREE.Vector3(), barycentric = new THREE.Vector3();
      for (const piece of group) {
        const position = piece.geometry.getAttribute('position'), indices = new Float32Array(position.count * 4), weights = new Float32Array(position.count * 4);
        for (let v = 0; v < position.count; v++) {
          p.fromBufferAttribute(position, v); let best = Infinity;
          for (let t = 0; t < triangles.length; t += 3) {
            const a = triangles[t], b = triangles[t + 1], c = triangles[t + 2]; triangle.set(nodes[a].rest, nodes[b].rest, nodes[c].rest);
            triangle.closestPointToPoint(p, closest); const distance = p.distanceToSquared(closest);
            if (distance >= best || !triangle.getBarycoord(closest, barycentric)) continue;
            best = distance;
            indices.set([a + cage.offset, b + cage.offset, c + cage.offset, 0], v * 4);
            weights.set([barycentric.x, barycentric.y, barycentric.z, 0], v * 4);
          }
        }
        piece.geometry.setAttribute(INDICES, new THREE.Float32BufferAttribute(indices, 4)); piece.geometry.setAttribute(WEIGHTS, new THREE.Float32BufferAttribute(weights, 4));
      }
    }
    data = new Float32Array(Math.max(1, total) * 8);
    if (skeleton) base = reserveTexels(skeleton, Math.max(1, total) * 2);
  }
  function pad(geometry: THREE.BufferGeometry) {
    if (geometry.hasAttribute(INDICES)) return;
    const count = geometry.getAttribute('position').count;
    geometry.setAttribute(INDICES, new THREE.Float32BufferAttribute(new Float32Array(count * 4), 4));
    geometry.setAttribute(WEIGHTS, new THREE.Float32BufferAttribute(new Float32Array(count * 4), 4));
  }
  function contact(point: THREE.Vector3, normal: THREE.Vector3, gap: number) {
    // Runs thousands of times per solve, so it works on plain numbers and skips the square roots for points already clear.
    const count = chain.length / 2;
    const knee = Math.floor(count / 2);
    // The cloth is pushed out along its level normal, less the part of it that runs along the bone.
    let hx = normal.x, hz = normal.z; const level = Math.sqrt(hx * hx + hz * hz);
    if (level > 0) { hx /= level; hz /= level; }
    for (let leg = 0; leg < 2; leg++) for (let bone = 0; bone < 2; bone++) {
      const a = chain[leg * count + (bone ? knee : 0)], b = chain[leg * count + (bone ? count - 1 : knee)];
      const abx = b.x - a.x, aby = b.y - a.y, abz = b.z - a.z, length = Math.max(abx * abx + aby * aby + abz * abz, 1e-8);
      let qx = point.x - a.x, qy = point.y - a.y, qz = point.z - a.z;
      const along = THREE.MathUtils.clamp((qx * abx + qy * aby + qz * abz) / length, 0, 1), radius = Math.max(0, a.w + (b.w - a.w) * along + gap);
      if (radius === 0) continue;
      qx -= abx * along; qy -= aby * along; qz -= abz * along;
      const radius2 = radius * radius, distance2 = qx * qx + qy * qy + qz * qz, share = (hx * abx + hz * abz) / length;
      let wx = hx - abx * share, wy = -aby * share, wz = hz - abz * share;
      if (wx * wx + wy * wy + wz * wz < .0001) { const up = aby / length; wx = -abx * up; wy = 1 - aby * up; wz = -abz * up; }
      // Outside the capsule on the side the cloth faces, the point is already clear: there |facing| is at least `outside` below.
      if (distance2 >= radius2 && qx * wx + qy * wy + qz * wz >= 0) continue;
      const w = Math.sqrt(wx * wx + wy * wy + wz * wz) || 1; wx /= w; wy /= w; wz /= w;
      const facing = qx * wx + qy * wy + qz * wz, beside2 = Math.max(0, distance2 - facing * facing);
      if (beside2 >= radius2) continue;
      const outside = Math.sqrt(radius2 - beside2);
      if (facing < outside) { const push = outside - facing; point.x += wx * push; point.y += wy * push; point.z += wz * push; }
    }
  }
  /** The solve's working state in flat arrays (x, y, z per node), built on the first solve after binding. */
  interface Layout {
    count: number; point: Float64Array; target: Float64Array; skin: Float64Array; normal: Float64Array; targetNormal: Float64Array; gap: Float64Array; pinned: Uint8Array;
    edgeA: Int32Array; edgeB: Int32Array; edgeRest: Float64Array; edgeShortest: Float64Array; dropA: Int32Array; dropB: Int32Array;
    triangles: Int32Array; seam: Int32Array; faceGaps: Float64Array;
    /** Per node, up to four bones with nonzero weight (a zero weight ends the list); `linear` is 0 where volume skinning applies. */
    bones: Int32Array; weights: Float64Array; linear: Uint8Array;
    /**
     * Within a solve, how often each point has moved, and the count at which its contact (and each face sample's, by the sum over its
     * corners) last found nothing to push. Contact depends on nothing else, so it is not tested again until one of its points moves.
     */
    moves: Uint32Array; clear: Int32Array; faceClear: Float64Array;
    /** `aim` for each node and, once a solve first needs it, each face sample (`faceAimed` holds that solve's number). */
    aims: Float64Array; faceAims: Float64Array; faceAimed: Uint32Array;
  }
  const layouts = new Map<Cage, Layout>(), scratch = new Float64Array(3);
  function layout(cage: Cage): Layout {
    const known = layouts.get(cage);
    if (known) return known;
    const count = cage.nodes.length, bones = new Int32Array(count * 4), weights = new Float64Array(count * 4), linear = new Uint8Array(count);
    cage.nodes.forEach((node, i) => {
      if (!node.mesh) return;
      const geometry = node.mesh.geometry, indices = geometry.getAttribute('skinIndex'), skinWeights = geometry.getAttribute('skinWeight');
      linear[i] = geometry.getAttribute(SKIN_VOLUME)?.getX(node.vertex) ? 0 : 1;
      for (let c = 0, k = 0; c < 4; c++) {
        const weight = skinWeights.getComponent(node.vertex, c); if (weight === 0) continue;
        bones[i * 4 + k] = indices.getComponent(node.vertex, c); weights[i * 4 + k] = weight; k++;
      }
    });
    const built: Layout = { count, point: new Float64Array(count * 3), target: new Float64Array(count * 3), skin: new Float64Array(count * 3),
      normal: new Float64Array(count * 3), targetNormal: new Float64Array(count * 3),
      gap: Float64Array.from(cage.nodes, node => node.gap), pinned: Uint8Array.from(cage.nodes, node => +node.pinned),
      edgeA: Int32Array.from(cage.edges, edge => edge[0]), edgeB: Int32Array.from(cage.edges, edge => edge[1]),
      edgeRest: Float64Array.from(cage.edges, edge => edge[2]), edgeShortest: Float64Array.from(cage.edges, edge => edge[3]),
      dropA: Int32Array.from(cage.drops, drop => drop[0]), dropB: Int32Array.from(cage.drops, drop => drop[1]),
      triangles: Int32Array.from(cage.triangles), seam: Int32Array.from(cage.seam.flat()), faceGaps: Float64Array.from(cage.faceGaps), bones, weights, linear,
      moves: new Uint32Array(count), clear: new Int32Array(count), faceClear: new Float64Array(cage.faceGaps.length),
      aims: new Float64Array(count * 24), faceAims: new Float64Array(cage.faceGaps.length * 24), faceAimed: new Uint32Array(cage.faceGaps.length) };
    cage.nodes.forEach((node, i) => { node.point.toArray(built.point, i * 3); node.target.toArray(built.target, i * 3); node.skinNormal.toArray(built.skin, i * 3); });
    layouts.set(cage, built);
    return built;
  }
  /** Each bone's skinning matrix, made once per solve instead of once per weight of every node. */
  const boneMatrices = new Map<THREE.Skeleton, { solve: number[]; matrices: THREE.Matrix4[] }>();
  let solves = 0;
  function boneMatrix(skeleton: THREE.Skeleton, bone: number) {
    let cache = boneMatrices.get(skeleton);
    if (!cache) { cache = { solve: [], matrices: [] }; boneMatrices.set(skeleton, cache); }
    const matrix = cache.matrices[bone] ??= new THREE.Matrix4();
    if (cache.solve[bone] !== solves) { matrix.multiplyMatrices(skeleton.bones[bone].matrixWorld, skeleton.boneInverses[bone]); cache.solve[bone] = solves; }
    return matrix.elements;
  }
  /**
   * `SkinnedMesh.applyBoneTransform` for a linear-skinned node, with the bone matrices shared across the solve: the same arithmetic in
   * the same order, so the same result. `w` is 1 for a point (skinned as a Vector3) and 0 for a direction (as a Vector4).
   */
  function skinNode(mesh: THREE.SkinnedMesh, l: Layout, node: number, x: number, y: number, z: number, w: number, out: Float64Array) {
    let e = mesh.bindMatrix.elements;
    const bx = e[0] * x + e[4] * y + e[8] * z + e[12] * w, by = e[1] * x + e[5] * y + e[9] * z + e[13] * w,
      bz = e[2] * x + e[6] * y + e[10] * z + e[14] * w, bw = e[3] * x + e[7] * y + e[11] * z + e[15] * w;
    let tx = 0, ty = 0, tz = 0;
    for (let c = 0; c < 4; c++) {
      const weight = l.weights[node * 4 + c]; if (weight === 0) break;
      const m = boneMatrix(mesh.skeleton, l.bones[node * 4 + c]);
      tx += (m[0] * bx + m[4] * by + m[8] * bz + m[12] * bw) * weight;
      ty += (m[1] * bx + m[5] * by + m[9] * bz + m[13] * bw) * weight;
      tz += (m[2] * bx + m[6] * by + m[10] * bz + m[14] * bw) * weight;
    }
    e = mesh.bindMatrixInverse.elements;
    if (w === 1) {
      // A Vector3's matrix product divides by the projected w.
      const d = 1 / (e[3] * tx + e[7] * ty + e[11] * tz + e[15]);
      out[0] = (e[0] * tx + e[4] * ty + e[8] * tz + e[12]) * d; out[1] = (e[1] * tx + e[5] * ty + e[9] * tz + e[13]) * d; out[2] = (e[2] * tx + e[6] * ty + e[10] * tz + e[14]) * d;
    } else {
      // A Vector4 gets its bind-space w back before the inverse.
      out[0] = e[0] * tx + e[4] * ty + e[8] * tz + e[12] * bw; out[1] = e[1] * tx + e[5] * ty + e[9] * tz + e[13] * bw; out[2] = e[2] * tx + e[6] * ty + e[10] * tz + e[14] * bw;
    }
  }
  /** The four leg capsules (thigh and shin of each leg) as `contact` reads them: a, b - a, the squared length and the radius change. */
  const capsules = new Float64Array(4 * 9);
  function measureCapsules() {
    const count = chain.length / 2, knee = Math.floor(count / 2);
    for (let leg = 0, k = 0; leg < 2; leg++) for (let bone = 0; bone < 2; bone++, k += 9) {
      const a = chain[leg * count + (bone ? knee : 0)], b = chain[leg * count + (bone ? count - 1 : knee)];
      const abx = b.x - a.x, aby = b.y - a.y, abz = b.z - a.z;
      capsules[k] = a.x; capsules[k + 1] = a.y; capsules[k + 2] = a.z; capsules[k + 3] = a.w;
      capsules[k + 4] = abx; capsules[k + 5] = aby; capsules[k + 6] = abz; capsules[k + 7] = Math.max(abx * abx + aby * aby + abz * abz, 1e-8); capsules[k + 8] = b.w - a.w;
    }
  }
  /**
   * The direction `contact` pushes a point with surface normal (nx, nz) out of each capsule, written at `at` as six values per capsule:
   * the raw direction, whose sign test clears most points, then the unit one, left NaN until a point needs it. It depends only on the
   * normal and the legs.
   */
  function aim(nx: number, nz: number, out: Float64Array, at: number) {
    let hx = nx, hz = nz; const level = Math.sqrt(hx * hx + hz * hz);
    if (level > 0) { hx /= level; hz /= level; }
    for (let c = 0; c < 4; c++) {
      const k = c * 9, abx = capsules[k + 4], aby = capsules[k + 5], abz = capsules[k + 6], length = capsules[k + 7], share = (hx * abx + hz * abz) / length;
      let wx = hx - abx * share, wy = -aby * share, wz = hz - abz * share;
      if (wx * wx + wy * wy + wz * wz < .0001) { const up = aby / length; wx = -abx * up; wy = 1 - aby * up; wz = -abz * up; }
      const o = at + c * 6;
      out[o] = wx; out[o + 1] = wy; out[o + 2] = wz; out[o + 3] = NaN;
    }
  }
  /** `contact` for point `i` of a flat array, along the directions `aim` wrote at `at`; returns whether the point moved. */
  function contactAt(points: Float64Array, i: number, aims: Float64Array, at: number, gap: number) {
    const o = i * 3;
    let px = points[o], py = points[o + 1], pz = points[o + 2], moved = false;
    for (let c = 0; c < 4; c++) {
      const k = c * 9, abx = capsules[k + 4], aby = capsules[k + 5], abz = capsules[k + 6], length = capsules[k + 7];
      let qx = px - capsules[k], qy = py - capsules[k + 1], qz = pz - capsules[k + 2];
      const along = Math.max(0, Math.min(1, (qx * abx + qy * aby + qz * abz) / length)), radius = Math.max(0, capsules[k + 3] + capsules[k + 8] * along + gap);
      if (radius === 0) continue;
      qx -= abx * along; qy -= aby * along; qz -= abz * along;
      const radius2 = radius * radius, distance2 = qx * qx + qy * qy + qz * qz, w = at + c * 6;
      if (distance2 >= radius2 && qx * aims[w] + qy * aims[w + 1] + qz * aims[w + 2] >= 0) continue;
      if (aims[w + 3] !== aims[w + 3]) {
        const rx = aims[w], ry = aims[w + 1], rz = aims[w + 2], unit = Math.sqrt(rx * rx + ry * ry + rz * rz) || 1;
        aims[w + 3] = rx / unit; aims[w + 4] = ry / unit; aims[w + 5] = rz / unit;
      }
      const wx = aims[w + 3], wy = aims[w + 4], wz = aims[w + 5];
      const facing = qx * wx + qy * wy + qz * wz, beside2 = Math.max(0, distance2 - facing * facing);
      if (beside2 >= radius2) continue;
      const outside = Math.sqrt(radius2 - beside2);
      if (facing < outside) { const push = outside - facing; px += wx * push; py += wy * push; pz += wz * push; moved = true; }
    }
    points[o] = px; points[o + 1] = py; points[o + 2] = pz;
    return moved;
  }
  /** Length links, then hanging drops; returns whether any point moved. */
  function constrain(l: Layout) {
    const { point, target, pinned, moves } = l; let moved = false;
    for (let k = 0; k < l.edgeA.length; k++) {
      const a = l.edgeA[k], b = l.edgeB[k], rest = l.edgeRest[k], shortest = l.edgeShortest[k], oa = a * 3, ob = b * 3;
      const abx = point[ob] - point[oa], aby = point[ob + 1] - point[oa + 1], abz = point[ob + 2] - point[oa + 2];
      // Most links sit within their slack: settle those without a square root.
      const length2 = abx * abx + aby * aby + abz * abz, low = rest * shortest, high = rest * 1.08;
      if (length2 >= low * low && length2 <= high * high) continue;
      const length = Math.sqrt(length2);
      if (length < 1e-8 || rest < 1e-8) continue;
      const wanted = Math.max(rest * shortest, Math.min(rest * 1.08, length)), change = (length - wanted) / length;
      const share = pinned[a] || pinned[b] ? 1 : .5, step = change * share, back = -change * share;
      if (!pinned[a] && step !== 0) { point[oa] += abx * step; point[oa + 1] += aby * step; point[oa + 2] += abz * step; moves[a]++; moved = true; }
      if (!pinned[b] && back !== 0) { point[ob] += abx * back; point[ob + 1] += aby * back; point[ob + 2] += abz * back; moves[b]++; moved = true; }
    }
    // Applied last, so length links cannot undo it: the lower point gives way, as hanging cloth does.
    for (let k = 0; k < l.dropA.length; k++) {
      const u = l.dropA[k] * 3, d = l.dropB[k] * 3;
      let ax = target[d] - target[u], ay = target[d + 1] - target[u + 1], az = target[d + 2] - target[u + 2];
      const drop = Math.sqrt(ax * ax + ay * ay + az * az); if (drop < 1e-8 || pinned[l.dropB[k]]) continue;
      const inverse = 1 / drop; ax *= inverse; ay *= inverse; az *= inverse;
      const short = drop * .5 - ((point[d] - point[u]) * ax + (point[d + 1] - point[u + 1]) * ay + (point[d + 2] - point[u + 2]) * az);
      if (short > 0) { point[d] += ax * short; point[d + 1] += ay * short; point[d + 2] += az * short; moves[l.dropB[k]]++; moved = true; }
    }
    return moved;
  }
  /** Joins each seam pair at its midpoint; returns whether any point moved. */
  function weld(l: Layout) {
    const { point, seam, moves } = l; let moved = false;
    for (let k = 0; k < seam.length; k += 2) for (let c = 0; c < 3; c++) {
      const a = seam[k] * 3 + c, b = seam[k + 1] * 3 + c;
      if (point[a] === point[b]) continue;
      point[a] = point[b] = (point[a] + point[b]) * .5; moves[seam[k]]++; moves[seam[k + 1]]++; moved = true;
    }
    return moved;
  }
  const facePoint = new Float64Array(3);
  /** Contact at sample points across each triangle, spread back to its corners; returns whether any point moved. */
  function faceContact(l: Layout) {
    const { point, skin, pinned, triangles, moves, faceClear, faceAims, faceAimed } = l; let moved = false;
    for (let t = 0; t < triangles.length; t += 3) for (let sample = 0; sample < faceSamples.length; sample++) {
      const weights = faceSamples[sample], at = t / 3 * faceSamples.length + sample, stamp = moves[triangles[t]] + moves[triangles[t + 1]] + moves[triangles[t + 2]];
      if (faceClear[at] === stamp) continue;
      let px = 0, py = 0, pz = 0, nx = 0, ny = 0, nz = 0, mobility = 0;
      for (let c = 0; c < 3; c++) {
        const node = triangles[t + c], o = node * 3, w = weights[c];
        px += point[o] * w; py += point[o + 1] * w; pz += point[o + 2] * w;
        nx += skin[o] * w; ny += skin[o + 1] * w; nz += skin[o + 2] * w;
        if (!pinned[node]) mobility += w ** 2;
      }
      if (!mobility) continue;
      if (faceAimed[at] !== solves) { const scale = 1 / (Math.sqrt(nx * nx + ny * ny + nz * nz) || 1); aim(nx * scale, nz * scale, faceAims, at * 24); faceAimed[at] = solves; }
      facePoint[0] = px; facePoint[1] = py; facePoint[2] = pz;
      if (!contactAt(facePoint, 0, faceAims, at * 24, l.faceGaps[at])) { faceClear[at] = stamp; continue; }
      const sx = facePoint[0] - px, sy = facePoint[1] - py, sz = facePoint[2] - pz;
      for (let c = 0; c < 3; c++) {
        const node = triangles[t + c]; if (pinned[node]) continue;
        const o = node * 3, share = weights[c] / mobility;
        point[o] += sx * share; point[o + 1] += sy * share; point[o + 2] += sz * share; moves[node]++; moved = true;
      }
    }
    return moved;
  }
  /** Unnormalised vertex normals of the cage's triangles at `from`, summed across the seam. */
  function cageNormals(l: Layout, from: Float64Array, sum: Float64Array) {
    const { triangles, seam } = l;
    sum.fill(0);
    for (let t = 0; t < triangles.length; t += 3) {
      const a = triangles[t] * 3, b = triangles[t + 1] * 3, c = triangles[t + 2] * 3;
      const ux = from[b] - from[a], uy = from[b + 1] - from[a + 1], uz = from[b + 2] - from[a + 2], vx = from[c] - from[a], vy = from[c + 1] - from[a + 1], vz = from[c + 2] - from[a + 2];
      const cx = uy * vz - uz * vy, cy = uz * vx - ux * vz, cz = ux * vy - uy * vx;
      sum[a] += cx; sum[a + 1] += cy; sum[a + 2] += cz; sum[b] += cx; sum[b + 1] += cy; sum[b + 2] += cz; sum[c] += cx; sum[c + 1] += cy; sum[c + 2] += cz;
    }
    for (let k = 0; k < seam.length; k += 2) for (let c = 0; c < 3; c++) { const a = seam[k] * 3 + c, b = seam[k + 1] * 3 + c; sum[a] = sum[b] = sum[a] + sum[b]; }
  }
  function update() {
    // Unchanged poses cost no solve or texture upload. A still body (a settled corpse, a held pose) rebuilds its bone
    // matrices every frame with rounding noise, so only a move past STILL from the pose last solved counts as a change.
    const count = chain.length * 4 + skinBones.size * 16;
    if (snapshot.length !== count) { snapshot = new Float64Array(count); ready = false; }
    let changed = !ready, cursor = 0;
    const check = (value: number) => { if (Math.abs(snapshot[cursor++] - value) > STILL) changed = true; };
    for (const v of chain) { check(v.x); check(v.y); check(v.z); check(v.w); }
    for (const bone of skinBones) for (const value of bone.matrixWorld.elements) check(value);
    if (!changed) return;
    cursor = 0;
    const keep = (value: number) => { snapshot[cursor++] = value; };
    for (const v of chain) { keep(v.x); keep(v.y); keep(v.z); keep(v.w); }
    for (const bone of skinBones) for (const value of bone.matrixWorld.elements) keep(value);
    ready = true; solves++; measureCapsules();
    for (const cage of cages) {
      const l = layout(cage), { point, target, skin } = l;
      for (let i = 0; i < l.count; i++) {
        const node = cage.nodes[i], mesh = node.mesh; if (!mesh) continue;
        const normal = mesh.geometry.getAttribute('normal'), o = i * 3;
        if (l.linear[i]) {
          skinNode(mesh, l, i, node.rest.x, node.rest.y, node.rest.z, 1, scratch);
          target[o] = point[o] = scratch[0]; target[o + 1] = point[o + 1] = scratch[1]; target[o + 2] = point[o + 2] = scratch[2];
          skinNode(mesh, l, i, normal.getX(node.vertex), normal.getY(node.vertex), normal.getZ(node.vertex), 0, scratch);
          p.set(scratch[0], scratch[1], scratch[2]);
        } else {
          node.target.copy(node.rest); mesh.applyBoneTransform(node.vertex, node.target); node.target.toArray(target, o); node.target.toArray(point, o);
          direction.set(normal.getX(node.vertex), normal.getY(node.vertex), normal.getZ(node.vertex), 0); mesh.applyBoneTransform(node.vertex, direction);
          p.set(direction.x, direction.y, direction.z);
        }
        p.normalize(); skin[o] = p.x; skin[o + 1] = p.y; skin[o + 2] = p.z;
      }
      for (let i = 0; i < l.count; i++) if (!l.pinned[i]) aim(skin[i * 3], skin[i * 3 + 2], l.aims, i * 24);
      // Every pass is deterministic, so an iteration that moves no point leaves the panel settled: later iterations would repeat it
      // exactly, except that every fourth one also samples the faces. A settled solve skips ahead to that one, and stops if it moves nothing.
      l.moves.fill(0); l.clear.fill(-1); l.faceClear.fill(-1);
      let settled = false;
      for (let iteration = 0; iteration < cage.iterations; iteration++) {
        const faces = iteration % 4 === 3;
        if (settled && !faces) continue;
        let moved = constrain(l);
        for (let i = 0; i < l.count; i++) {
          if (l.pinned[i] || l.clear[i] === l.moves[i]) continue;
          if (contactAt(point, i, l.aims, i * 24, l.gap[i])) { l.moves[i]++; moved = true; } else l.clear[i] = l.moves[i];
        }
        if (faces && faceContact(l)) moved = true;
        if (weld(l)) moved = true;
        if (!moved && faces) break;
        settled = !moved;
      }
      // Contact may demand more fabric than the panel owns. Spread the final correction
      // instead of accepting a tight capsule-shaped ridge at the last projection.
      for (let i = 0; i < cage.iterations * .75; i++) if (!constrain(l)) break;
      // The closing column must end where the opening one does, or the seam opens.
      weld(l);
      cageNormals(l, point, l.normal); cageNormals(l, target, l.targetNormal);
      const { normal, targetNormal } = l;
      for (let i = 0; i < l.count; i++) {
        const node = cage.nodes[i], offset = (cage.offset + i) * 8, o = i * 3;
        node.point.fromArray(point, o); node.target.fromArray(target, o); node.skinNormal.fromArray(skin, o);
        data[offset] = point[o] - target[o]; data[offset + 1] = point[o + 1] - target[o + 1]; data[offset + 2] = point[o + 2] - target[o + 2];
        // Preserve authored folds: apply the cage's normal change, rather than replacing fine normals.
        const n = 1 / (Math.sqrt(normal[o] * normal[o] + normal[o + 1] * normal[o + 1] + normal[o + 2] * normal[o + 2]) || 1);
        const m = 1 / (Math.sqrt(targetNormal[o] * targetNormal[o] + targetNormal[o + 1] * targetNormal[o + 1] + targetNormal[o + 2] * targetNormal[o + 2]) || 1);
        data[offset + 4] = normal[o] * n - targetNormal[o] * m;
        data[offset + 5] = normal[o + 1] * n - targetNormal[o + 1] * m;
        data[offset + 6] = normal[o + 2] * n - targetNormal[o + 2] * m;
      }
    }
    if (skeleton) { figureTexels(skeleton).set(data, base * 4); touchTexels(skeleton); }
  }
  /** Add the cage to a material. It reads the points from whichever figure it draws, so figures with the same skeleton can share it. */
  function apply(material: THREE.Material) {
    if (base < 0) throw new Error('Prepare the cage on its skeleton before applying it to a material');
    const compile = material.onBeforeCompile, key = material.customProgramCacheKey.bind(material), first = base;
    material.customProgramCacheKey = () => `${key()}:cloth-cage-2@${first}`;
    material.onBeforeCompile = (shader, renderer) => {
      compile.call(material, shader, renderer);
      shader.vertexShader = withFigureTexels(`attribute vec4 ${INDICES}; attribute vec4 ${WEIGHTS};\n` + shader.vertexShader).replace('void main() {', `
        #ifdef USE_SKINNING
        vec3 cageField(int row) { vec3 field=vec3(0.); for(int i=0;i<4;i++) field+=figureTexel(${first}+int(${INDICES}[i])*2+row).xyz*${WEIGHTS}[i]; return field; }
        #endif
        void main() {`);
      shader.vertexShader = shader.vertexShader.replace('if (clothGap > 0.5)', `if (clothGap > 0.5 && dot(${WEIGHTS},vec4(1.)) < .5)`)
        .replace('#include <skinnormal_vertex>', '#include <skinnormal_vertex>\n#ifdef USE_SKINNING\nobjectNormal += cageField(1);\n#endif')
        .replace('#include <skinning_vertex>', '#include <skinning_vertex>\n#ifdef USE_SKINNING\ntransformed += cageField(0);\n#endif');
    };
  }
  function displace(geometry: THREE.BufferGeometry, index: number, point: THREE.Vector3) {
    const indices = geometry.getAttribute(INDICES), weights = geometry.getAttribute(WEIGHTS); if (!indices || !weights) return point;
    for (let c = 0; c < 4; c++) { const offset = indices.getComponent(index, c) * 8, weight = weights.getComponent(index, c); point.x += data[offset] * weight; point.y += data[offset + 1] * weight; point.z += data[offset + 2] * weight; }
    return point;
  }
  /** Attach a merged batch; returns whether any of its vertices follow a cage. */
  function bind(mesh: THREE.SkinnedMesh, geometries: THREE.BufferGeometry[]) {
    const cageWeights = mesh.geometry.getAttribute(WEIGHTS);
    let offset = 0, caged = false;
    for (let i = 0; i < cageWeights.count && !caged; i++) caged = cageWeights.getX(i) + cageWeights.getY(i) + cageWeights.getZ(i) > 0;
    for (const geometry of geometries) {
      for (const cage of cages) for (const node of cage.nodes) if (node.geometry === geometry) {
        node.vertex += offset; node.mesh = mesh;
        const indices = mesh.geometry.getAttribute('skinIndex'), weights = mesh.geometry.getAttribute('skinWeight');
        for (let c = 0; c < 4; c++) if (weights.getComponent(node.vertex, c)) skinBones.add(mesh.skeleton.bones[indices.getComponent(node.vertex, c)]);
      }
      offset += geometry.getAttribute('position').count;
    }
    ready = false; layouts.clear();
    if (!caged) return false;
    mesh.geometry.userData.nimblezAnimated = true;
    const original = mesh.getVertexPosition;
    mesh.getVertexPosition = function (index, target) { return displace(this.geometry, index, original.call(this, index, target)); };
    return true;
  }
  /** Once every batch is bound, the source pieces were build-time data only: let them go. */
  function release() { pieces.length = 0; for (const cage of cages) for (const node of cage.nodes) node.geometry = undefined; }
  return { register, prepare, pad, update, apply, bind, displace, release, cages };
}
